import { useMemo } from 'react'
import { Box, LinearProgress, Skeleton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { AUTHENTICATION_CONFIG, AUTHENTICATION_METHODS, getAuthenticationTranslationKey } from './constants'
import { AuthenticationMethod, Ssid } from './types'

interface AuthenticationSummaryProps {
  loading: boolean
  ssids: Ssid[]
}

const AuthenticationSummarySkeleton = () => (
  <Box
    sx={{
      display: 'grid',
      gap: 1.5,
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(3, minmax(0, 1fr))'
      }
    }}>
    {AUTHENTICATION_METHODS.map(authenticationMethod => (
      <Box
        key={authenticationMethod}
        sx={{
          border: '1px solid #EFEFEF',
          borderRadius: '16px',
          p: 2
        }}>
        <Skeleton variant="rounded" height={40} width={40} />
        <Skeleton height={28} sx={{ mt: 1.5 }} width="45%" />
        <Skeleton height={18} width="70%" />
        <Skeleton height={10} sx={{ mt: 1 }} width="100%" />
      </Box>
    ))}
  </Box>
)

const AuthenticationSummary = ({ loading, ssids }: AuthenticationSummaryProps) => {
  const { t } = useTranslation()

  const authenticationCounts = useMemo(
    () =>
      ssids.reduce<Record<AuthenticationMethod, number>>(
        (counts, ssid) => {
          counts[ssid.auth_method] += 1
          return counts
        },
        { open: 0, psk: 0, '802.1x': 0 }
      ),
    [ssids]
  )

  if (loading) {
    return <AuthenticationSummarySkeleton />
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(3, minmax(0, 1fr))'
        }
      }}>
      {AUTHENTICATION_METHODS.map(authenticationMethod => {
        const config = AUTHENTICATION_CONFIG[authenticationMethod]
        const authenticationCount = authenticationCounts[authenticationMethod]
        const percentage = ssids.length === 0 ? 0 : Math.round((authenticationCount / ssids.length) * 100)
        const translationKey = getAuthenticationTranslationKey(authenticationMethod)

        return (
          <Box
            key={authenticationMethod}
            sx={{
              border: '1px solid #EFEFEF',
              borderRadius: '16px',
              p: 2
            }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: config.backgroundColor,
                  borderRadius: '10px',
                  color: config.color,
                  display: 'flex',
                  height: 40,
                  justifyContent: 'center',
                  width: 40
                }}>
                <i aria-hidden="true" className={`fa-light fa-${config.icon}`} style={{ fontSize: 17 }} />
              </Box>
              <Typography sx={{ color: '#989898', fontSize: 11, fontWeight: 500 }}>{percentage}%</Typography>
            </Stack>

            <Typography sx={{ color: '#464646', fontSize: 22, fontWeight: 600, mt: 1.5 }}>
              {authenticationCount}
            </Typography>
            <Typography sx={{ color: '#656565', fontSize: 11, fontWeight: 500 }}>
              {t(`ssid_monitoring.authentication.${translationKey}`)}
            </Typography>
            <Typography
              sx={{
                color: '#989898',
                fontSize: 10,
                fontWeight: 400,
                minHeight: 30,
                mt: 0.5
              }}>
              {t(`ssid_monitoring.authentication_description.${translationKey}`)}
            </Typography>
            <LinearProgress
              aria-label={t(`ssid_monitoring.authentication.${translationKey}`)}
              variant="determinate"
              value={percentage}
              sx={{
                backgroundColor: config.backgroundColor,
                borderRadius: 10,
                height: 4,
                mt: 1,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: config.color,
                  borderRadius: 10
                }
              }}
            />
          </Box>
        )
      })}
    </Box>
  )
}

export default AuthenticationSummary
